import React from "react";
import {
  Card,
  CardContent,
  Avatar,
  Box,
  Typography,
  Chip,
  Button,
  Grid
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Business as BuildingIcon,
  Email as MailIcon,
  Phone as PhoneIcon,
  Work as BriefcaseIcon,
  VerifiedUser as ShieldCheckIcon,
  Event as CalendarIcon,
  LocationOn as MapPinIcon,
  OpenInNew as ExternalLinkIcon
} from "@mui/icons-material";

const EmployeeView = ({ data }) => {
  const theme = useTheme();
  if (!data) {
    return null;
  }

  const employee = Array.isArray(data) ? data[0] : data;
  const initials = `${employee.first_name?.charAt(0) || ''}${employee.last_name?.charAt(0) || ''}`.toUpperCase();

  const InfoItem = ({ icon, label, value, iconColor }) => {
    if (!value) return null;
    return (
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
          {React.cloneElement(icon, { sx: { color: iconColor } })}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
              {value}
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  };

  return (
    <Card elevation={1} sx={{ borderRadius: 4, mb: 2, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: theme.shadows[3] } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, alignItems: 'flex-start' }}>
          
          {/* Avatar */}
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: 3, 
              bgcolor: 'primary.main',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              boxShadow: theme.shadows[2],
              fontSize: '2rem',
              fontWeight: 'bold'
            }}
          >
            {initials || employee.name?.charAt(0)}
          </Avatar>

          {/* Main Info */}
          <Box sx={{ flex: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={700}>
                {employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim()}
              </Typography>
              {employee.is_active !== false && (
                <Chip 
                  label="Actif" 
                  size="small" 
                  color="success" 
                  sx={{ fontWeight: 600, bgcolor: 'success.50', color: 'success.700', '& .MuiChip-label': { px: 1.5 } }} 
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, color: 'text.secondary' }}>
              <BriefcaseIcon fontSize="small" />
              <Typography variant="body2">
                {employee.job_title || employee.position || employee.title}
              </Typography>
            </Box>

            {/* Details Grid */}
            <Grid container spacing={2}>
              <InfoItem icon={<BuildingIcon />} label="Département" value={employee.department} iconColor={theme.palette.primary.main} />
              <InfoItem icon={<MailIcon />} label="Email" value={employee.email} iconColor={theme.palette.info.main} />
              <InfoItem icon={<PhoneIcon />} label="Téléphone" value={employee.phone} iconColor={theme.palette.secondary.main} />
              <InfoItem icon={<ShieldCheckIcon />} label="Manager" value={employee.manager_name} iconColor={theme.palette.warning.main} />
              <InfoItem 
                icon={<CalendarIcon />} 
                label="Date d'embauche" 
                value={employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('fr-FR') : null} 
                iconColor={theme.palette.success.main} 
              />
              <InfoItem icon={<MapPinIcon />} label="Localisation" value={employee.location} iconColor={theme.palette.error.main} />
            </Grid>
          </Box>
        </Box>

        {/* Action Button */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            variant="text" 
            endIcon={<ExternalLinkIcon />}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Voir le profil complet
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmployeeView;
